/**
 * Drawing Tool Color Utilities and Swatch Palette.
 *
 * Implements TradingView-style HSL to Hex color conversions and the 80-color
 * swatch grid generator (1 grayscale row + 7 saturated rows).
 */

export const SWATCH_HUES = [
  0, 25, 45, 90, 160, 190, 220, 255, 285, 330,
] as const;

export const SWATCH_ROW_LIGHTNESS = [85, 70, 60, 50, 42, 32, 22] as const;

/**
 * Converts HSL color values (H: 0-360, S: 0-100, L: 0-100) to hex string format (#rrggbb).
 */
export function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) =>
    lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Generates the canonical 80-color swatch palette for drawing tool popovers.
 */
export function generateSwatchHexList(): string[] {
  const swatches: string[] = [];

  // Row 1: Grayscale (white -> black)
  for (let i = 0; i < 10; i++) {
    swatches.push(hslToHex(0, 0, Math.round(100 - (i * 100) / 9)));
  }

  // Rows 2-8: 10 hues at decreasing lightness
  for (const l of SWATCH_ROW_LIGHTNESS) {
    for (const h of SWATCH_HUES) {
      swatches.push(hslToHex(h, 65, l));
    }
  }

  return swatches;
}
